/**
 * UI State Manager
 * Manages UI state, view switching, and error banners
 *
 * @module UIStateManager
 * Sprint 32: OOA/OOD Refactoring
 */

import { ViewType, ClaudeStatus } from '../types/ii-agent-types';

/**
 * Interface for UI state manager
 */
export interface IUIStateManager {
    currentView: ViewType;
    switchView(view: ViewType): void;
    showErrorBanner(title: string, message: string, details?: string): void;
    hideErrorBanner(): void;
    updateBackendIndicator(status: ClaudeStatus | null, aiBackend: string): void;
}

/**
 * Manager for UI state and view management
 */
export class UIStateManager implements IUIStateManager {
    currentView: ViewType = 'chat';
    private onViewChange?: (view: ViewType) => void;

    constructor(onViewChange?: (view: ViewType) => void) {
        this.onViewChange = onViewChange;
    }

    /**
     * Switch between views
     */
    switchView(view: ViewType): void {
        this.currentView = view;

        // Update tab styling
        const tabOverview = document.getElementById('tab-overview');
        const tabChat = document.getElementById('tab-chat');
        const tabHistory = document.getElementById('tab-history');

        const selectedClass = 'px-4 py-2 text-blue-600 border-b-2 border-blue-600 font-medium';
        const unselectedClass = 'px-4 py-2 text-gray-500 hover:text-blue-600 transition-colors';

        if (tabOverview) tabOverview.className = view === 'overview' ? selectedClass : unselectedClass;
        if (tabChat) tabChat.className = view === 'chat' ? selectedClass : unselectedClass;
        if (tabHistory) tabHistory.className = view === 'history' ? selectedClass : unselectedClass;

        // Update views
        document.querySelectorAll('[data-view-content]').forEach(content => {
            content.classList.toggle('hidden', (content as HTMLElement).dataset.viewContent !== view);
        });

        // Trigger callback if provided
        if (this.onViewChange) {
            this.onViewChange(view);
        }
    }

    /**
     * Show error/info banner
     */
    showErrorBanner(title: string, message: string, details?: string): void {
        const banner = document.getElementById('ada-error-banner');
        const titleEl = document.getElementById('error-title');
        const messageEl = document.getElementById('error-message');
        const detailsEl = document.getElementById('error-details');
        const iconEl = document.getElementById('error-icon');

        if (!banner || !titleEl || !messageEl) {
            console.warn('Error banner elements not found');
            return;
        }

        titleEl.textContent = title;
        messageEl.textContent = message;

        if (detailsEl && details) {
            detailsEl.textContent = details;
            detailsEl.classList.remove('hidden');
        } else if (detailsEl) {
            detailsEl.classList.add('hidden');
        }

        // Set icon based on error type
        if (iconEl) {
            iconEl.textContent = title.toLowerCase().includes('error') ? '!' : 'i';
        }

        // Style the banner
        banner.className = 'mb-4 p-4 rounded-lg border bg-amber-50 border-amber-200';
        banner.classList.remove('hidden');
    }

    /**
     * Hide error banner
     */
    hideErrorBanner(): void {
        const banner = document.getElementById('ada-error-banner');
        if (banner) {
            banner.classList.add('hidden');
        }
    }

    /**
     * Update the backend status indicator in the UI
     */
    updateBackendIndicator(status: ClaudeStatus | null, aiBackend: string): void {
        const indicator = document.getElementById('backend-indicator');
        const label = document.getElementById('backend-label');

        if (!indicator || !label) {
            console.warn('Backend indicator elements not found');
            return;
        }

        if (aiBackend === 'claude' && status?.configured && status?.enabled) {
            // Claude is configured and enabled
            indicator.className = 'w-2 h-2 rounded-full bg-green-500';
            label.textContent = 'Claude AI';
            label.className = 'text-sm text-green-600';
        } else if (aiBackend === 'claude' && !status?.configured) {
            // Claude selected but not configured
            indicator.className = 'w-2 h-2 rounded-full bg-red-500';
            label.textContent = 'Claude (Not Configured)';
            label.className = 'text-sm text-red-600';
            this.showErrorBanner(
                'Claude AI Not Configured',
                'Claude API key has not been set. Using pattern-based responses as fallback.',
                'Go to Settings > AI Backend to configure Claude API key.'
            );
        } else if (aiBackend === 'llm') {
            // Self-hosted LLM
            indicator.className = 'w-2 h-2 rounded-full bg-blue-500';
            label.textContent = 'Self-Hosted LLM';
            label.className = 'text-sm text-blue-600';
        } else {
            // Local pattern-based
            indicator.className = 'w-2 h-2 rounded-full bg-amber-500';
            label.textContent = 'Basic Mode';
            label.className = 'text-sm text-gray-600';
        }
    }

    /**
     * Initialize settings service reference
     */
    getAiBackend(): string {
        // Get AI backend setting from SettingsService
        if (typeof window !== 'undefined' && (window as any).SettingsService) {
            const settingsService = new (window as any).SettingsService();
            return settingsService.getAiBackend() || 'local';
        }
        return 'local';
    }
}
